import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Link } from 'react-router-dom';
import FirebaseImage from '../components/FirebaseImage';

const DashboardPage = () => {
  const { currentUser, userRole } = useAuth();
  const [recentBooks, setRecentBooks] = useState([]);
  const [recommendedBooks, setRecommendedBooks] = useState([]);
  const [stats, setStats] = useState({
    booksRead: 0,
    readingTime: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!currentUser || !currentUser.uid) {
          setError('Authentication required. Please log in again.');
          setLoading(false);
          return;
        }

        console.log('Fetching dashboard data for user:', currentUser.uid);
        
        // Directly query Firestore for userBooks collection - simplify query to avoid needing composite index
        const userBooksRef = collection(db, 'userBooks');
        const userBooksQuery = query(
          userBooksRef,
          where('userId', '==', currentUser.uid)
        );
        
        const userBooksSnapshot = await getDocs(userBooksQuery);
        console.log(`Found ${userBooksSnapshot.size} books for user ${currentUser.uid}`);
        
        // Array to store books with complete details
        const readBooks = [];
        
        // Process each user book record
        for (const userBookDoc of userBooksSnapshot.docs) {
          const userBookData = userBookDoc.data();
          
          // Only include books marked as read
          if (!userBookData.read) {
            continue;
          }
          
          console.log('Processing user book:', userBookData);
          
          try {
            // Get the full book details from the books collection
            const bookRef = doc(db, 'books', userBookData.bookId);
            const bookDoc = await getDoc(bookRef);
            
            if (bookDoc.exists()) {
              const bookData = bookDoc.data();
              
              // Only include books that are fully uploaded (have both cover image and text content)
              if (bookData.coverImageUrl && bookData.textContent) {
              // Combine book data with reading information
              readBooks.push({
                ...bookData,
                id: bookDoc.id,
                bookId: bookDoc.id,
                lastReadAt: userBookData.lastReadAt,
                readingTime: userBookData.readingTime || 10 // Default to 10 minutes if not specified
              });
              }
            } else {
              console.warn(`Book ${userBookData.bookId} not found in books collection`);
            }
          } catch (err) {
            console.error(`Error fetching book ${userBookData.bookId}:`, err);
          }
        }
        
        // Sort read books by lastReadAt (most recent first)
        readBooks.sort((a, b) => {
          const dateA = a.lastReadAt ? 
            (a.lastReadAt.toDate ? a.lastReadAt.toDate() : new Date(a.lastReadAt)) : 
            new Date(0);
          const dateB = b.lastReadAt ? 
            (b.lastReadAt.toDate ? b.lastReadAt.toDate() : new Date(b.lastReadAt)) : 
            new Date(0);
          return dateB - dateA;
        });
        
        console.log('Processed read books:', readBooks);
        
        // Set recent books (up to 3)
        setRecentBooks(readBooks.slice(0, 3));
        
        // Update stats
        setStats({
          booksRead: readBooks.length,
          readingTime: readBooks.reduce((sum, book) => sum + (book.readingTime || 0), 0)
        });

        // Fetch recommended books (books not read by the user)
        const booksRef = collection(db, 'books');
        const booksSnapshot = await getDocs(booksRef);
        const allBooks = booksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter out books the user has already read and ensure books have textContent
        const readBookIds = new Set(readBooks.map(book => book.bookId));
        const unreadBooks = allBooks
          .filter(book => !readBookIds.has(book.id))
          .filter(book => book.coverImageUrl && book.textContent); // Only include fully uploaded books
        
        // Recommend up to 3 unread books
        setRecommendedBooks(unreadBooks.slice(0, 3));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{error}</h2>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light py-12">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800">
              Welcome, {currentUser?.displayName || 'Reader'}!
            </h1>
            <p className="text-gray-600">
              {userRole === 'student' ? 'Student Dashboard' : 
               userRole === 'teacher' ? 'Teacher Dashboard' : 'Admin Dashboard'}
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <Card className="text-center p-6">
                <h3 className="text-lg font-semibold text-gray-700">Books Read</h3>
                <p className="text-4xl font-bold text-primary mt-2">{stats.booksRead}</p>
              </Card>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Card className="text-center p-6">
                <h3 className="text-lg font-semibold text-gray-700">Reading Time</h3>
                <p className="text-4xl font-bold text-primary mt-2">{stats.readingTime} minutes</p>
              </Card>
            </motion.div>
          </div>

          {/* Recent Books Section */}
          <div className="mb-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Your Recent Books</h2>
              <Link to="/books">
                <Button variant="secondary">View All Books</Button>
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentBooks.length > 0 ? (
                recentBooks.map((book, index) => (
                  <motion.div
                    key={book.id || book.bookId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 + 0.3 }}
                  >
                    <Card className="flex p-4">
                      {book.coverImagePath ? (
                        <FirebaseImage 
                          path={book.coverImagePath} 
                          alt={book.title} 
                          className="w-20 h-28 object-cover rounded-md"
                          quality="low"
                          width="80"
                          height="112"
                          fallbackSrc="/images/book-placeholder.jpg"
                        />
                      ) : (
                        <img 
                          src={book.coverUrl || book.coverImageUrl || "/images/book-placeholder.jpg"} 
                          alt={book.title} 
                          className="w-20 h-28 object-cover rounded-md"
                          width="80"
                          height="112"
                        />
                      )}
                      <div className="ml-4 flex flex-col justify-between">
                        <div>
                          <h3 className="font-bold text-gray-800">{book.title}</h3>
                          <p className="text-sm text-gray-600">{book.author}</p>
                        </div>
                        <div className="mt-2">
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full bg-secondary-green mr-2"></div>
                            <span className="text-xs text-gray-600">Read</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1">
                            Last read: {book.lastReadAt ? 
                              (book.lastReadAt.toDate ? 
                                book.lastReadAt.toDate().toLocaleDateString() : 
                                new Date(book.lastReadAt).toLocaleDateString()) : 
                              'Unknown'}
                          </p>
                        </div>
                        <Link to={`/books/${book.bookId || book.id}`} className="text-primary text-sm mt-2">
                          View Book
                        </Link>
                      </div>
                    </Card>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-3 text-center py-8">
                  <p className="text-gray-600 mb-4">You haven't read any books yet</p>
                  <Link to="/books">
                    <Button variant="primary">Explore Books</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Recommended Section */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Recommended for You</h2>
            {recommendedBooks.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recommendedBooks.map((book, index) => (
                  <motion.div
                    key={book.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 + 0.3 }}
                  >
                    <Card className="flex p-4">
                      {book.coverImagePath ? (
                        <FirebaseImage 
                          path={book.coverImagePath} 
                          alt={book.title} 
                          className="w-20 h-28 object-cover rounded-md"
                          quality="low"
                          width="80"
                          height="112"
                          fallbackSrc="/images/book-placeholder.jpg"
                        />
                      ) : (
                        <img 
                          src={book.coverUrl || book.coverImageUrl || "/images/book-placeholder.jpg"} 
                          alt={book.title} 
                          className="w-20 h-28 object-cover rounded-md"
                          width="80"
                          height="112"
                        />
                      )}
                      <div className="ml-4 flex flex-col justify-between">
                        <div>
                          <h3 className="font-bold text-gray-800">{book.title}</h3>
                          <p className="text-sm text-gray-600">{book.author}</p>
                        </div>
                        <Link to={`/books/${book.id}`} className="text-primary text-sm mt-2">
                          View Book
                        </Link>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-6">
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">
                    Based on your reading history, we think you'll enjoy these books
                  </p>
                  <Link to="/books">
                    <Button variant="primary">Explore Recommendations</Button>
                  </Link>
                </div>
              </Card>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage; 